import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { zonedTimeToUtc } from 'date-fns-tz';

import { Button } from '@components/common';
import FormWithTabs from '@components/common/FormWithTabs';
import Dependencies from '@components/ModalComponents/TaskForm/Dependencies';

import { portalTasks as portalTasksSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { clearLinkedTasks, setLinkedTasks } from '@slices/portalTasks';

import { projectUsersFormatted } from '@utils/formatter';
import { formatDate, parseStringToDateInMilliseconds } from '@utils/kpi-utils/date';

import { IAssetFile } from '@interfaces/assets/asset';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ITaskFormValues } from '@interfaces/components/form';
import {
	ICreateTaskDTO,
	IEditTaskDTO,
	IPortalTaskDTO,
} from '@interfaces/portal/dto/taskManagementDTO';

import { EValidationMode } from '@enums/validationMode';

import {
	formatTemplateDefault,
	portalTasksPriorities as portalTasksPriorityValues,
	portalTasksStatuses as portalTasksStatusValues,
	timezoneUTC,
} from '@constants/common';
import { tabsNames } from './constants';

import BasicInfo from './BasicInfo';
import Details from './Details';
import { getSchema } from './schema';
import { formatDefaultSelectOption } from './utils';

import styles from './TaskForm.module.scss';

interface ITaskFormProps {
	closeModals: () => void;
	createTask?: (data: ICreateTaskDTO) => Promise<void>;
	defaultProject?: TSelectOption<string>;
	isEditMode?: boolean;
	isViewMode?: boolean;
	portalTask?: IPortalTaskDTO;
	projectId?: string;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	toggleCloseConfirmModal: () => void;
	updateTask?: (data: IEditTaskDTO) => Promise<void>;
	portalTasksStatuses: TSelectOption[];
	portalTasksPriorities: TSelectOption[];
}

const TaskForm = ({
	projectId,
	closeModals,
	setIsDirtyFormModal,
	isEditMode = false,
	isViewMode,
	toggleCloseConfirmModal,
	portalTask,
	createTask,
	updateTask,
	defaultProject,
	portalTasksStatuses,
	portalTasksPriorities,
}: ITaskFormProps) => {
	const dispatch = useAppDispatch();

	const { linkedTasks } = useAppSelector(portalTasksSelector);

	const [uploadedFiles, setUploadedFiles] = useState<Array<IAssetFile>>([]);

	const defaultStatusOption = portalTasksStatuses.find(item => item?.name === portalTasksStatusValues.toDo);
	const defaultPriorityOption = portalTasksPriorities.find(item => item?.name === portalTasksPriorityValues.medium);

	const statusDefaultValue = formatDefaultSelectOption(portalTask?.status || defaultStatusOption);
	const priorityDefaultValue = formatDefaultSelectOption(portalTask?.priority || defaultPriorityOption);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		control,
		setValue,
		watch,
		trigger,
		clearErrors,
		reset,
	} = useForm<ITaskFormValues>({
		resolver: yupResolver(getSchema(isEditMode)),
		mode: EValidationMode.all,
		defaultValues: {
			project: defaultProject,
			title: portalTask?.title,
			description: portalTask?.description,
			status: statusDefaultValue,
			priority: priorityDefaultValue,
			milestone: portalTask?.milestone && {
				...portalTask.milestone,
				value: portalTask.milestone.id,
				label: portalTask.milestone.title,
			},
			startDate: portalTask?.startDate ? parseStringToDateInMilliseconds(portalTask.startDate) : null,
			endDate: portalTask?.endDate ? parseStringToDateInMilliseconds(portalTask.endDate) : null,
			assignees: portalTask?.assignees && projectUsersFormatted(portalTask.assignees),
		},
	});

	useEffect(() => {
		if (portalTask?.attachments && (isViewMode || isEditMode)) {
			setUploadedFiles(portalTask.attachments);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (portalTask?.linkedTasks && (isViewMode || isEditMode)) {
			dispatch(setLinkedTasks({ linkedTasks: portalTask.linkedTasks }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isBasicInfoTabError = !!(errors.project || errors.title || errors.description);

	const isDetailsTabError = !!(errors.status
		|| errors.priority
		|| errors.milestone
		|| errors.startDate
		|| errors.endDate
		|| errors.assignees
	);

	const clearForm = useCallback(() => {
		reset({
			title: '',
			description: '',
		});

		setUploadedFiles([]);

		dispatch(clearLinkedTasks());
	}, [dispatch, reset]);

	const onSubmit = useCallback((values: ITaskFormValues) => {
		const projectId = values?.project?.value;

		const data = {
			assignees: values?.assignees?.map(assignee => assignee.value),
			attachments: uploadedFiles.map(file => file.id),
			description: values?.description,
			priority: values.priority?.value || '',
			status: values.status?.value || '',
			title: values.title,
			startDate: values?.startDate
				? formatDate(zonedTimeToUtc(values.startDate, timezoneUTC), formatTemplateDefault)
				: null,
			endDate: values?.endDate
				? formatDate(zonedTimeToUtc(values.endDate, timezoneUTC), formatTemplateDefault)
				: null,
			linkedTasks: linkedTasks.map(linkedTask => {
				return {
					id: linkedTask.id,
					type: linkedTask.type.name,
					isPredecessor: linkedTask.isPredecessor,
				};
			}),
			milestoneId: values.milestone?.value,
		};

		if (isEditMode) {
			if (!portalTask?.id || !updateTask) return;

			void updateTask({
				taskId: portalTask.id,
				dto: data,
			});
		} else {
			if (!projectId || !createTask) return;

			void createTask({
				...data,
				projectId,
			});
		}

		closeModals();
	}, [
		linkedTasks,
		closeModals,
		createTask,
		isEditMode,
		portalTask?.id,
		updateTask,
		uploadedFiles,
	]);

	const tabs = useMemo(() => (
		[
			{
				title: 'Basic information',
				value: tabsNames.basicInfo,
				component: BasicInfo,
				isTabError: isBasicInfoTabError,
				props: {
					register,
					errors,
					setValue,
					trigger,
					control,
					isBasicInfoTabError,
					portalTask,
					watch,
					isDirty,
					clearForm,
				},
			},
			{
				title: 'Details',
				value: tabsNames.details,
				component: Details,
				isTabError: isDetailsTabError,
				props: {
					errors,
					setValue,
					watch,
					trigger,
					control,
					clearErrors,
					isDetailsTabError,
					projectId,
					portalTasksStatuses,
					portalTasksPriorities,
				},
			},
			{
				title: 'Dependencies & Attachments',
				value: tabsNames.dependencies,
				component: Dependencies,
				isTabError: false,
				props: {
					setUploadedFiles,
					uploadedFiles,
					onSubmit: handleSubmit(onSubmit),
					projectId,
					taskId: portalTask?.id,
					watch,
				},
			},
		]
	), [
		clearErrors,
		control,
		watch,
		register,
		errors,
		handleSubmit,
		setValue,
		trigger,
		onSubmit,
		portalTask,
		uploadedFiles,
		isBasicInfoTabError,
		isDetailsTabError,
		projectId,
		clearForm,
		isDirty,
		portalTasksPriorities,
		portalTasksStatuses,
	]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		return () => {
			dispatch(clearLinkedTasks());
		};
	}, [dispatch]);

	return (
		<>
			<FormWithTabs
				isEditMode={isEditMode}
				isViewMode={isViewMode}
				tabs={tabs}
			/>
			{isEditMode
				&& <div className={styles.buttons}>
					<Button
						onClick={toggleCloseConfirmModal}
						text='Cancel'
						withoutBackground
					/>
					<Button
						onClick={handleSubmit(onSubmit)}
						text='Save'
					/>
				</div>
			}
			{isViewMode
				&& <div className={styles.buttons}>
					<Button
						onClick={closeModals}
						text='Cancel'
					/>
				</div>
			}
		</>
	);
};

export default TaskForm;
